import { StorageAdapter } from '../storage/StorageAdapter'

/**
 * MetaRepository — marcas de idempotencia clave/valor sobre SQLite (CAP-05).
 *
 * Tabla `meta(key TEXT PRIMARY KEY, value TEXT)` (schema.js:113). Almacen generico
 * key->value que el drain de F3 usa para DEDUPLICAR efectos secundarios no-idempotentes
 * (eventos que no estan cubiertos por un UNIQUE en la BD): antes de re-emitir un efecto
 * comprueba `has(key)`; al confirmarlo escribe `put(key)`. NO importa v1 ni el plugin:
 * solo emite SQL via StorageAdapter (STOR-05).
 *
 * PREFIJOS DE CLAVE LOCKED (CAP-05) — el vocabulario de marcas de idempotencia:
 *   - `occ-closed:<occId>`           ocurrencia cerrada (occurrence.close ya aplicado).
 *   - `evsent:<uuid>`                evento de outbox ya enviado (firma/evidencia/etc.).
 *   - `tstatus:<id>:<state>`         cambio de estado de tarea ya aplicado.
 *   - `tcreated:<client_uuid>`       tarea creada (mitiga el POST tasks no-dedup del backend).
 *   - `tedit:<client_uuid>`          edicion de tarea ya aplicada.
 *   - `cursor:<catalog>`             (RESERVADO para F4 — cursor de pull de catalogos).
 *   - `cursor:fail:<catalog>`        contador de fallos + ventana de backoff del pull de ese
 *                                    catalogo (sync/pull/pull-backoff.js, TKT-2240).
 *   - `prefetch:form:<formId>`       epoch ms del ultimo warm-up completo de groups+items
 *                                    (catalogs.prefetchFormChildren — skip-frescos del prefetch).
 *   - `apcat:<orgId>:<type>`         cache read-through del config de org (ap_categories/
 *                                    ap_categories2) para el dialogo de tarea (domain/catalogs.js).
 *   - `hiersitecat:<siteId>`         cache de categorias-por-sitio de la jerarquia (domain/catalogs.js).
 *   - `areas:<orgId>:<category>`     cache de fetchAreas?category= (domain/catalogs.js).
 *   - `warm:apcat:<orgId>`           epoch ms del ultimo warm-up de org-config (sync/pull/org-config.js).
 *   - `tassign:<client_uuid>`        tarea de plan de accion creada SIN red: su usuario inicial queda
 *                                    pendiente hasta que haya lista de usuarios (TKT-1975, domain/tasks.js).
 *
 * FRONTERA F2/F3: F3 ESCRIBE estas marcas al confirmar cada efecto en el push exitoso;
 * F2 solo provee el almacen (put/get/has + los key-builders). Las llamadas son F3.
 *
 * Lectura defensiva: el backend SQLite nativo (@capacitor-community/sqlite) `query` devuelve `{ values: [...] }`; sql.js (en
 * el gate) difiere — por eso el parseo del shape vive DENTRO del repo (es el codigo real).
 */
class MetaRepositoryClass {
  /** Upsert idempotente de una marca; value por defecto '1' (presencia booleana). */
  async put (key, value = '1') {
    await StorageAdapter.run(
      'INSERT INTO meta (key, value) VALUES (?, ?) ' +
      'ON CONFLICT(key) DO UPDATE SET value = excluded.value',
      [key, value],
      true
    )
  }

  /** Valor de una marca, o null si no existe. */
  async get (key) {
    const result = await StorageAdapter.query(
      'SELECT value FROM meta WHERE key = ?',
      [key]
    )
    const row = result && result.values && result.values[0]
    return row ? row.value : null
  }

  /** True si la marca existe (efecto ya aplicado). */
  async has (key) {
    return (await this.get(key)) !== null
  }

  /** Borra una marca (idempotente: borrar la inexistente es no-op). */
  async del (key) {
    await StorageAdapter.run('DELETE FROM meta WHERE key = ?', [key], true)
  }

  /** Claves que empiezan con `prefix` (el prefijo no lleva comodines: se escapan % y _). */
  async keysWithPrefix (prefix) {
    const escaped = String(prefix).replace(/[\\%_]/g, match => `\\${match}`)
    const result = await StorageAdapter.query(
      "SELECT key FROM meta WHERE key LIKE ? ESCAPE '\\'",
      [`${escaped}%`]
    )
    return ((result && result.values) || []).map(row => row.key)
  }

  // ── Key-builders de las marcas locked (CAP-05) ───────────────────────────────
  occClosedKey (occId) { return `occ-closed:${occId}` }
  evSentKey (uuid) { return `evsent:${uuid}` }
  taskStatusKey (id, state) { return `tstatus:${id}:${state}` }
  taskCreatedKey (clientUuid) { return `tcreated:${clientUuid}` }
  taskEditKey (clientUuid) { return `tedit:${clientUuid}` }
  taskAssignmentPendingKey (clientUuid) { return `tassign:${clientUuid}` }
}

export const MetaRepository = new MetaRepositoryClass()
